import {
    Component,
    ComponentInterface,
    Prop,
    h,
    Event,
    Host,
} from "@stencil/core";
import type { EventEmitter } from "@stencil/core"

import type { BotId } from "../../../../../../global/models/models";
import {
    Suggestion,
    StartThemeStampLabels,
} from "../../../../../../utils/backend/reply-parsed";

@Component({
    tag: "c-suggestions",
    styleUrl: "c-suggestions.scss",
})
export class CSuggestions implements ComponentInterface {
    @Prop() elements: Suggestion[];
    @Prop() introText: boolean | undefined;
    @Prop() botId?: BotId;
    @Prop() btnContext?: string;
    @Prop() theme?: string;
    @Prop() icon?: string;
    @Prop() stage?: string;
    @Prop() germanTitle?: string;
    @Event() linkSuggestionSelected: EventEmitter;
    @Event() buttonSuggestionSelected: EventEmitter;

    private changeLang(lang: string) {
        const bot = document.getElementsByTagName("ba-chatbot")[0];
        bot.setAttribute("page-id", "form/form-" + lang);
    }

    private readonly saveLS = (element) => {
        if (element.stage) localStorage.setItem("FormState", element.stage);
        if (this.btnContext) {
            localStorage.setItem(this.btnContext, element.title);
            if (element.german) {
                localStorage.setItem("DE_" + this.btnContext, element.german);
            }
        }
        this.buttonSuggestionSelected.emit(element);
    };

    buildSuggestions = (suggestions: Suggestion[], big?: boolean) => {
        return suggestions.map((suggestion) => {
            const commonClass = (base: string) => {
                const sizeClass = big ? `big-${base}` : `small-${base}`;
                const themeClass = suggestion.theme ? suggestion.theme : "";
                return [base, sizeClass, themeClass].filter(Boolean).join(" ");
            };

            const innerContent = `
            <span class="sr-only">Klicken sie für: ${suggestion.screenReader || suggestion.title}</span>
            <span aria-hidden="true">${suggestion.title}</span>
        `;

            const commonData = {
                "data-german": suggestion.german,
                innerHTML: innerContent
            };

            switch (suggestion.type) {
                case "link":
                case "conversationLink":
                    return (
                        <li>
                            <a
                                {...commonData}
                                class={commonClass("suggestion-link")}
                                href={suggestion.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() =>
                                    this.linkSuggestionSelected.emit(suggestion)
                                }
                                data-analytics-track="true"
                                data-analytics-action="LinkClicks TCB: Klick"
                                data-analytics-value={suggestion.url}
                                data-analytics-name="TCB: Message Link"
                                data-analytics-category="TCB | LinkClicks"
                            >
                                <i
                                    id="link-icon"
                                    aria-hidden="true"
                                    class="link-icon"
                                ></i>
                            </a>
                        </li>
                    );

                case "button":
                    return (
                        <li>
                            <button
                                {...commonData}
                                class={commonClass("suggestion-button")}
                                onClick={() => this.saveLS(suggestion)}
                                data-analytics-track="true"
                                data-analytics-action="ButtonClicks TCB: Klick"
                                data-analytics-value={suggestion}
                                data-analytics-name="TCB: Suggestion Button"
                                data-analytics-category="TCB | ButtonClicks"
                            ></button>
                        </li>
                    );

                case "form": {
                    const lang = suggestion.payload === "start_formular_ua" ? "ua" : "de";
                    return (
                        <button
                            class={commonClass("suggestion-button")}
                            innerHTML={innerContent}
                            onClick={() => this.changeLang(lang)}
                        ></button>
                    );
                }
            }
        });
    };

    getSuggestionsStampText(suggNum: number) {
        if (this.introText === false || suggNum === 0 || !this.botId) {
            return;
        }
        let introText: string;
        if (suggNum === 1) {
            introText = "Das könnte Sie auch interessieren:";
        } else {
            introText = `Weitere ${StartThemeStampLabels[this.botId]} Themen:`;
        }
        return (
            <c-stamp-text
                stamp={introText}
                variant="suggestions-intro"
            ></c-stamp-text>
        );
    }

    render() {
        let smallItems: any;
        let bigItem: any;
        // We create a local copy to safely manipulate part of answerBy
        const rawSuggestions = [...this.elements];
        // For now we only hanlde the case where we have (max) 1 fullWith button/link
        const bigItemIndex = rawSuggestions.findIndex(
            (el) => el.fullWidth === true
        );
        if (bigItemIndex === -1) {
            smallItems = this.buildSuggestions(rawSuggestions);
        } else {
            bigItem = rawSuggestions.splice(bigItemIndex, 1);
            smallItems = this.buildSuggestions(rawSuggestions);
        }

        return (
            <Host>
                {bigItem && this.buildSuggestions(bigItem, true)}
                {this.getSuggestionsStampText(smallItems.length)}
                {smallItems}
            </Host>
        );
    }
}
